import type { PartlyReadCheck, ScanFreshness } from '../../lib/api'
import { listOf } from './notLicensed'

/*
 * CHECKS THAT RAN AND SAW ONLY PART OF WHAT THEY NEEDED (review, 2026-09-27).
 *
 * An area can finish with every check run while one of them could not read, or could not judge,
 * part of what it needed: a read cut short, a value Microsoft did not state, a row the check could
 * not classify. Its findings are reported and it closes nothing it did not see, but the area is
 * "complete" -- so a low count there looks exactly like a healthy area.
 *
 * The dashboard said "2 areas read only part of what Microsoft holds" and nothing else: not which
 * areas, not which checks, not why. The server now names the checks by title (areas[].partlyRead,
 * read back out of the area's stored sentence); this file is where the pages turn that into words.
 *
 * WHY is said only as far as it was recorded. New scans can carry fixed codes and counts. Legacy
 * scans have no per-check reason; it is never inferred from the current implementation.
 */

/** One area where some checks saw only part of what they needed. */
export interface PartlyReadArea {
  area: string

  /** The checks, by title, in the order the scan recorded them. */
  checks: PartlyReadCheck[]

  /**
   * The area's stored sentence, when no check could be named from it (an older server, or a
   * sentence in a form the server does not recognise). Shown rather than dropped: the area still
   * counts, and a count with nothing behind it is the gap this replaced. Null when `checks` names them.
   */
  unnamed: string | null
}

/**
 * The areas where some checks saw only part of what they needed.
 *
 * On a complete assessment, every area carrying a detail -- the rule the count has always used, so
 * the count cannot shrink. Elsewhere (`finishedOnly`), only areas that finished: an area that did not
 * finish carries a sentence about the checks that could not run, which the could-not-read card names.
 */
export function partlyReadAreas(
  freshness: ScanFreshness,
  { finishedOnly = false }: { finishedOnly?: boolean } = {},
): PartlyReadArea[] {
  return freshness.areas.flatMap((a) => {
    const checks = (a.partlyRead ?? []).filter((c) => checkName(c).trim() !== '')

    if (checks.length === 0 && !a.detail) return []
    if (finishedOnly && a.status !== 'complete') return []

    return [{ area: a.area, checks, unnamed: checks.length > 0 ? null : (a.detail ?? null) }]
  })
}

/**
 * The phrase for a status line: "1 check in Endpoint saw only part of what it needed", "3 checks in
 * Endpoint and Identity saw only part of what they needed". "Some checks" where any area's checks
 * could not be named, since a number would then be a guess. Empty when there are none.
 */
export function partlyReadPhrase(areas: PartlyReadArea[]): string {
  if (areas.length === 0) return ''

  const where = listOf(areas.map((a) => a.area))
  if (areas.some((a) => a.checks.length === 0)) {
    return `some checks in ${where} saw only part of what they needed`
  }

  const n = areas.reduce((sum, a) => sum + a.checks.length, 0)
  return n === 1
    ? `1 check in ${where} saw only part of what it needed`
    : `${n} checks in ${where} saw only part of what they needed`
}

/**
 * The line under one area's row on the dashboard: "1 check saw only part of what it needed." Beside
 * the area's own count, because that count is where a low number would be read as a healthy area.
 */
export function partlyReadNote(area: PartlyReadArea): string {
  const n = area.checks.length
  if (n === 0) return 'Some checks saw only part of what they needed.'
  return n === 1
    ? '1 check saw only part of what it needed.'
    : `${n} checks saw only part of what they needed.`
}

/**
 * Areas that finished where the record of which checks saw only part of what they needed exists but
 * could not be read (areas[].detailUnreadable): a key gone, or a row sealed under an earlier binding.
 *
 * Not among partlyReadAreas, because nothing here can say which checks, or whether any. But not
 * whole either: the record being there at all is the scan having had something to say. So the pages
 * say they could not confirm the area was read in full, rather than printing that it was.
 */
export function unconfirmedAreas(freshness: ScanFreshness): string[] {
  return freshness.areas
    .filter((a) => a.status === 'complete' && a.detailUnreadable === true)
    .map((a) => a.area)
}

/** A check's name on the page: its title, or its key when the catalogue has no title for it. */
export function checkName(check: PartlyReadCheck): string {
  return check.title.trim() || check.key
}

/**
 * The consequence common to every partial verdict. Reasons are shown per check only when recorded.
 */
export const PARTLY_READ_WHY =
  'These checks ran, and what they found is listed with your other findings. Each one could not ' +
  'read, or could not judge, part of what it needed. Nothing they found before has been closed.'

/** Diagnostic text is ours, from an allowlist. Never render arbitrary API reason text or raw values. */
export function gapDescriptions(check: PartlyReadCheck): string[] {
  if (!check.gaps?.length) return ['For this check, this assessment does not record which part or why. A new assessment may provide more detail.']
  return check.gaps.map((gap) => {
    if (!Number.isSafeInteger(gap.count) || gap.count < 1) return 'A recorded gap count could not be interpreted. Coverage remains unconfirmed for this check.'
    const records = `${gap.count} record${gap.count === 1 ? '' : 's'}`
    const messages: Record<string, string> = {
      inventory_truncated: `The inventory read stopped after ${gap.count} returned records; it did not reach the end.`,
      sensor_health_unknown: `${records} had healthStatus missing or outside the values this check can judge.`,
      sensor_inactive: `Microsoft reported ${gap.count} device record${gap.count === 1 ? '' : 's'} as inactive; current protection cannot be confirmed. This does not establish that ${gap.count === 1 ? 'the device is' : 'the devices are'} powered off or broken.`,
      onboarding_unknown: `${records} had onboardingStatus missing or outside the values this check can judge.`,
      last_seen_unknown: `${records} had lastSeen missing or not readable as a date.`,
      antivirus_mode_unknown: `${records} had avMode missing or outside the values this check can judge.`,
      signature_freshness_unknown: `${records} did not state avIsSignatureUpToDate as true or false.`,
      engine_freshness_unknown: `${records} did not state avIsEngineUpToDate as true or false.`,
      platform_freshness_unknown: `${records} did not state avIsPlatformUpToDate as true or false.`,
      assignments_unattributed: `${gap.count} permission grant(s) or role member(s) could not be attributed to an identity.`,
      application_details_unreadable: `${gap.count} permission/role holder(s) could not be fully read or identified.`,
      credentials_undecidable: `${gap.count} permission/role holder(s) had credential data this check could not judge.`,
      holder_limit_reached: `${gap.count} permission/role holder(s) were beyond this check's assessment limit.`,
      agent_credentials_out_of_scope: `${gap.count} agent or blueprint ${gap.count === 1 ? 'identity was' : 'identities were'} outside this check's credential scope; their credentials were not assessed.`,
    }
    return Object.hasOwn(messages, gap.code) ? messages[gap.code]! : 'This build does not recognise a recorded gap reason. Coverage remains unconfirmed for this check.'
  })
}

/** Said plainly, because a low count is exactly what a partly read area and a healthy one share. */
export const PARTLY_READ_NOT_BETTER = 'Fewer findings in these areas does not mean better security.'
